import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

export default function Brands() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/brands").then(({ data }) => {
      setBrands(data);
      setLoading(false);
    });
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-[68px] lg:pt-[116px]" data-testid="brands-page">
      {/* Hero */}
      <section className="bg-[#0A2540] py-20 text-white sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-overline text-[#34D8A0]">Trusted formulas · every therapist recommends</p>
          <h1 className="mt-4 font-display text-5xl font-black leading-[1.05] tracking-tight sm:text-7xl">
            The brands<br />behind our promise.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70">
            Every brand we carry is chosen by physiotherapists — formulated for real recovery, not marketing.
          </p>
        </div>
      </section>

      {/* Brand grid */}
      <section className="mx-auto max-w-[1400px] px-5 py-16 sm:px-8 sm:py-24">
        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-[4/5] animate-pulse bg-black/5" />
            ))}
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {brands.map((b, i) => (
              <motion.div
                key={b.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to={`/brands/${b.slug}`}
                  className="group relative block overflow-hidden border border-black/10 bg-white transition-all hover:border-[#2F80ED]"
                  data-testid={`brand-card-${b.slug}`}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[#F9F9F6] to-white" />
                  {b.image && (
                    <img
                      src={b.image}
                      alt={b.name}
                      className="relative aspect-[4/3] w-full object-contain p-10 transition-transform duration-700 group-hover:scale-105"
                    />
                  )}
                  <div className="relative flex items-end justify-between border-t border-black/10 p-6">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-overline text-[#2F80ED]">Formula house</div>
                      <div className="mt-2 font-display text-2xl font-bold tracking-tight text-[#0A2540] group-hover:text-[#2F80ED]">
                        {b.name}
                      </div>
                      <div className="mt-2 flex items-center gap-3 font-mono text-xs text-black/50">
                        <span>{b.count} formula{b.count > 1 ? "s" : ""}</span>
                        <span className="flex items-center gap-1">
                          <Star className="h-3 w-3 fill-[#2F80ED] text-[#2F80ED]" /> {b.rating}
                        </span>
                      </div>
                    </div>
                    <ArrowUpRight className="h-6 w-6 shrink-0 text-black/30 transition-all group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-[#2F80ED]" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
